import type { ButtonState, Colors } from '@finetic/waimo-ui'
import type { NavigationFailure } from 'vue-router'

export enum EntityTypeOf {
  ACCOUNTING = 'accounting',
  ALLOCATION = 'allocation',
  ALLOCATION_REQUEST = 'allocation_request',
  CHAT = 'chat',
  CLAIM = 'claim',
  CLIENT = 'client',
  CLIENT_ADDRESS = 'client_address',
  CLIENT_CONTACT = 'client_contact',
  COMPLAINT = 'complaint',
  CONTRACT = 'contract',
  CONTRACT_EXCLUSION = 'contract_exclusion',
  CREDITOR = 'creditor',
  FULFILLMENT = 'fulfillment',
  IBERICHTEN = 'imessage',
  FILE = 'file',
  MUNICIPALITY = 'municipality',
  MUNICIPALITY_CONTACT = 'municipality_contact',
  PAYMENT = 'payment',
  PRODUCT_CONTRACT_PRICE = 'product_contract_price',
  PRODUCT_PRICE = 'product_price',
  PRODUCT_ROLE = 'product_role',
  QUOTATION = 'quotation',
  ROLE = 'role',
  SUPPLIER = 'supplier',
  SUPPLIER_CONTACT = 'supplier_contact',
  SUPPLIER_LOCATION = 'supplier_location',
  SUPPLIER_SPECIALIZATION = 'supplier_specialization',
  PRODUCT = 'product',
  TASK = 'task',
  USER = 'user',
}

export enum EntityAction {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
}

export type ActionType = () => Promise<
  void | NavigationFailure | undefined
> | void

export type PrimaryAction = {
  text: string
  action: ActionType
  state?: ButtonState
}

export type SecondaryAction = Array<
  | {
    text: string
    action: ActionType
    icon?: string
    color?: Colors
    state?: ButtonState
  }
  | undefined
>

export type EntityRelation = {
  relationType: EntityTypeOf
  name: string
  id: string
}

export type Signals = {
  // From API
  openComplaints?: Signal
  openQuotations?: Signal
  openAllocationRequests?: Signal
  openClaims?: Signal
  activeContracts?: Signal
  activeAllocations?: Signal
  activeAllocationsWithoutSupplierSince?: Signal
  activeExclusions?: Signal
  numberOfProducts?: Signal
  rejectedFulfillments?: Signal
  numberOfUsers?: Signal
  clientBirthDateMismatch?: Signal
  clientMunicipalityMismatch?: Signal
  clientGenderMismatch?: Signal
  // Calculated by FE
  supplierSinceUnset?: Signal // `allocation.since` in past && `allocation.supplierSince` is undefined
  allocationLength?: Signal // amount of days in allocation - from `allocation.dashboardData.allocationLengthInDays`. Formatted string. Show if allocation is not revoked
  allocationStartsIn?: Signal // amount of days before `allocation.since`. Formatted string. Show if in future.
  allocationEndsIn?: Signal // amount of days before `allocation.until`. Formatted string. Show if `allocation.since` is in past and `allocation.until` is set.
  allocationRequestOpen?: Signal // if `allocationReqeust.status` !== active || denied
  allocationRequestAssignedTo?: Signal // based on allocationReqeust.allocationRequestStats === REQUESTED_AT_MUNICIPALITY or REQUESTED_AT_SUPPLIER
  quotationOpen?: Signal // true if `quotation.status` === added
  complaintOpen?: Signal // true if `complaint.status` === RECEIVED_BY_COMPLAINTS_CENTER
  complaintAssignedTo?: Signal // based on `complaint.complaintStatus === RECEIVED_BY_COMPLAINTS_CENTER or FORWARDED_TO_SUPPLIER or UNDER_REVIEW_BY_SUPPLIER
  contractLength?: Signal // amount of days between `contract.since` and `contract.until`. Formatted string. Show if both are set
  contractStartsIn?: Signal // amount of days before `contract.since`. Formatted string. Show if in future.
  contractEndsIn?: Signal // amount of days before `contract.until`. Formatted string. Show if `contract.since` is in past and `contract.until` is set.
  numberOfRoles?: Signal //amount of roles a user has.
  unknownClient?: Signal // `allocationRequest.client` === undefined | null
}

export type Signal = {
  value: boolean | string | number
  icon?: string
  action?: () => Promise<void | NavigationFailure | undefined> | void
}

export enum EntityStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export type SortBy =
  | {
    field: string
    direction: 'ASC' | 'DESC'
  }
  | undefined

export type SearchResult = Record<EntityTypeOf, EntityRelation[]>

export enum Gender {
  UNKNOWN = 'unknown',
  MALE = 'male',
  FEMALE = 'female',
}
